
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useAppStore } from '../../store';
import { UserProfile } from '@repo/shared-types';
import { useToast } from '../../components/common/ToastContext';
import { userProfileSchema } from '@repo/shared-types/identity/schemas';
import { validateEntity, logger } from '@repo/shared-utils';

export const useCurrentUser = () => {
  const currentUserId = useAppStore(state => state.currentUserId);

  return useQuery({
    queryKey: ['users', currentUserId],
    queryFn: async () => {
      const data = await api.users.get(currentUserId);
      if (data) {
        const validation = validateEntity(userProfileSchema, data);
        if (!validation.valid) {
          logger.error('Invalid current user query response:', validation.errors);
          // Return placeholder data on validation failure
          return {
            id: currentUserId,
            firstName: 'Guest',
            lastName: 'User',
            fullName: 'Guest User',
            email: 'guest@example.com',
            avatar: '',
            phone: '',
            emailVerified: false,
            lastLoginAt: '',
            twoFactorEnabled: false,
            role: 'TENANT' as const,
          } as UserProfile;
        }
        return validation.data as UserProfile;
      }
      // Return placeholder data if no data
      return {
        id: currentUserId,
        firstName: 'Guest',
        lastName: 'User',
        fullName: 'Guest User',
        email: 'guest@example.com',
        avatar: '',
        phone: '',
        emailVerified: false,
        lastLoginAt: '',
        twoFactorEnabled: false,
        role: 'TENANT' as const,
      } as UserProfile;
    },
    enabled: !!currentUserId,
    // Intentionally no placeholderData: React Query treats placeholder rows as
    // "resolved", which flips isLoading to false and flashes "Guest User" in the
    // sidebar on every refresh until the real profile arrives. Callers that care
    // about pre-hydration state must read isLoading / data === undefined.
  });
};

export const useUser = (userId?: string) => {
  return useQuery({
    queryKey: ['users', userId],
    queryFn: async () => {
      if (userId == null) throw new Error('userId required');
      const data = await api.users.get(userId);
      // ✅ Validate query response with Zod schema
      if (data) {
        const validation = validateEntity(userProfileSchema, data);
        if (!validation.valid) {
          logger.error('Invalid user query response:', validation.errors);
          throw new Error('Invalid user data received from API');
        }
        return validation.data as UserProfile;
      }
      return null;
    },
    enabled: !!userId,
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<UserProfile> }) => api.users.update(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['users', data.id] });
      showToast('Profile updated successfully', 'success');
    },
    onError: () => {
      showToast('Failed to update profile', 'error');
    }
  });
};


